import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Save, RefreshCw, ChevronLeft, ChevronRight, X, Download, Search } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/admin';
import { Button, Select, Card, Badge, Spinner, Modal } from '@/components/common';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { stundenzettel as api } from '@/utils/api';
import { toast } from 'sonner';
import { getMonthName } from '@/utils/formatters';
import { useStundenzettelOverview, useStundenzettelEmployees } from '@/hooks/useTimeEntries';

// Absence codes with German labels
const CODES = [
  { value: '', label: '-' },
  { value: 'K', label: 'K - Krank' },
  { value: 'U', label: 'U - Urlaub' },
  { value: 'F', label: 'F - Feiertag' },
  { value: 'S', label: 'S - Sonstiges' },
  { value: 'UB', label: 'UB - Unbez. Urlaub' },
  { value: 'E', label: 'E - Elternzeit' },
  { value: 'MS', label: 'MS - Mutterschutz' },
  { value: 'X', label: 'X - Ausgeschieden' },
  // "Ü" ist KEIN Abwesenheitscode (#137): der Tag wurde gearbeitet, die Zeiten
  // bleiben stehen. Es ist ein interner Merker fuers Buero und wird deshalb auch
  // NICHT in den PDF-Stundenzettel gedruckt (services/stundenzettelPdfService.js).
  { value: 'Ü', label: 'Ü - Überstunden' },
];

// Codes where no working time should be recorded ("Ü" gehoert bewusst nicht dazu)
const NO_WORK_CODES = ['K', 'U', 'F', 'UB', 'E', 'MS', 'X'];

const CODE_OPTIONS = CODES.map(c => ({ value: c.value, label: c.value || '-' }));

// German public holidays for Berlin/Brandenburg
const getGermanHolidays = (year) => {
  const holidays = {};

  // Fixed holidays
  holidays[`${year}-01-01`] = 'Neujahr';
  holidays[`${year}-05-01`] = 'Tag der Arbeit';
  holidays[`${year}-10-03`] = 'Tag der Deutschen Einheit';
  holidays[`${year}-12-25`] = '1. Weihnachtstag';
  holidays[`${year}-12-26`] = '2. Weihnachtstag';

  // Easter-based holidays (calculate Easter Sunday)
  const easter = calculateEaster(year);
  const easterDate = new Date(year, easter.month, easter.day);

  // Karfreitag (Good Friday) - 2 days before Easter
  const karfreitag = new Date(easterDate);
  karfreitag.setDate(karfreitag.getDate() - 2);
  holidays[formatDateISO(karfreitag)] = 'Karfreitag';

  // Ostermontag (Easter Monday) - 1 day after Easter
  const ostermontag = new Date(easterDate);
  ostermontag.setDate(ostermontag.getDate() + 1);
  holidays[formatDateISO(ostermontag)] = 'Ostermontag';

  // Christi Himmelfahrt (Ascension Day) - 39 days after Easter
  const himmelfahrt = new Date(easterDate);
  himmelfahrt.setDate(himmelfahrt.getDate() + 39);
  holidays[formatDateISO(himmelfahrt)] = 'Christi Himmelfahrt';

  // Pfingstmontag (Whit Monday) - 50 days after Easter
  const pfingstmontag = new Date(easterDate);
  pfingstmontag.setDate(pfingstmontag.getDate() + 50);
  holidays[formatDateISO(pfingstmontag)] = 'Pfingstmontag';

  return holidays;
};

// Calculate Easter Sunday using the Anonymous Gregorian algorithm
const calculateEaster = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1; // 0-indexed
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
};

// Format date as YYYY-MM-DD without timezone issues
const formatDateISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Get days in month
const getDaysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

// Check if date is weekend (Saturday or Sunday)
const isWeekend = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  const dow = date.getDay();
  return dow === 0 || dow === 6;
};

// Get day of week abbreviation in German
const getDayAbbrev = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return days[date.getDay()];
};

const Stundenzettel = () => {
  const queryClient = useQueryClient();
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [employeeId, setEmployeeId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [entries, setEntries] = useState({});
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const pendingChangesRef = useRef({});
  // Keep ref in sync with state for saveChanges to always read latest
  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
  }, [pendingChanges]);

  // Generate year options (current year +/- 2)
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentDate.getFullYear() - 2; y <= currentDate.getFullYear() + 1; y++) {
      years.push({ value: y, label: String(y) });
    }
    return years;
  }, []);

  // Generate month options
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: getMonthName(i + 1),
    }));
  }, []);

  // Get holidays for current year
  const holidays = useMemo(() => getGermanHolidays(year), [year]);

  // Calculate days in current month
  const daysInMonth = useMemo(() => getDaysInMonth(year, month), [year, month]);

  // Load employees via hook
  const { data: employeesData = [], isLoading: employeesLoading } = useStundenzettelEmployees();
  const employees = employeesData;

  // Set default employeeId once employees are loaded
  useEffect(() => {
    if (employees.length > 0 && !employeeId) {
      setEmployeeId(employees[0].user_id);
    }
  }, [employees]);

  // Load overview via hook
  const { data: overviewData, isLoading: overviewLoading } = useStundenzettelOverview({ year, month });
  const overview = overviewData?.employees ?? overviewData ?? [];
  const loading = overviewLoading || employeesLoading;

  // Load calendar entries when modal opens or employee/month/year changes
  useEffect(() => {
    if (!calendarModalOpen || !employeeId) return;

    api.getEntries({ user_id: employeeId, year, month })
      .then(response => {
        const entriesMap = {};
        (response.data || []).forEach(entry => {
          const day = entry.date ? parseInt(entry.date.split('-')[2]) : null;
          if (day) {
            entriesMap[day] = {
              id: entry.id,
              date: entry.date,
              code: entry.code,
              startTime: entry.start_time,
              endTime: entry.end_time,
              breakMinutes: entry.break_minutes,
              workMinutes: entry.work_minutes,
              remark: entry.remark,
            };
          }
        });
        setEntries(entriesMap);
        setPendingChanges({});
        pendingChangesRef.current = {};
      })
      .catch(err => {
        console.error('[Stundenzettel] Load entries error:', err);
        setEntries({});
        setPendingChanges({});
        pendingChangesRef.current = {};
      });
  }, [year, month, employeeId, calendarModalOpen]);

  // Mutations
  const bulkUpdateMutation = useMutation({
    mutationFn: (payload) => api.bulkUpdate(payload),
    onSuccess: () => {
      setPendingChanges({});
      pendingChangesRef.current = {};
      toast.success('Gespeichert');
      queryClient.invalidateQueries({ queryKey: ['stundenzettel'] });
    },
    onError: (err: any) => {
      console.error('[Stundenzettel] Save error:', err);
      toast.error(err.message || 'Fehler beim Speichern');
    },
  });

  const syncMutation = useMutation({
    mutationFn: (payload) => api.sync(payload),
    onSuccess: (response) => {
      const data = response.data;
      toast.success(data.message || `Sync abgeschlossen: ${data.updated} Einträge aktualisiert`);
      queryClient.invalidateQueries({ queryKey: ['stundenzettel'] });
    },
    onError: (err: any) => {
      console.error('[Stundenzettel] Sync error:', err);
      toast.error(err.message || 'Fehler beim Synchronisieren');
    },
  });

  // Debounced auto-save - uses ref to always get latest pendingChanges
  const saveChanges = useCallback(async () => {
    // Read from ref to get latest accumulated changes (not stale closure)
    const currentPending = pendingChangesRef.current;
    if (Object.keys(currentPending).length === 0) return;

    // Convert entries to backend format with date in YYYY-MM-DD format
    const entriesToSave = Object.entries(currentPending).map(([day, data]: [string, any]) => {
      const dayNum = parseInt(day);
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      return {
        date: dateStr,
        code: data.code || null,
        start_time: data.startTime || null,
        end_time: data.endTime || null,
        break_minutes: data.breakMinutes ? parseInt(data.breakMinutes) : null,
        work_minutes: data.workMinutes || null,
        remark: data.remark || null,
      };
    });

    console.log('[Stundenzettel] Saving entries:', entriesToSave.length, 'entries:', entriesToSave.map(e => `${e.date}: ${e.code || 'work'}`));

    bulkUpdateMutation.mutate({ user_id: employeeId, year, month, entries: entriesToSave });
  }, [employeeId, year, month]); // Removed pendingChanges from deps - using ref instead

  // Auto-save after 2 seconds of no changes
  useEffect(() => {
    if (Object.keys(pendingChanges).length === 0) return;

    const timer = setTimeout(() => {
      console.log('[Stundenzettel] Auto-save triggered with', Object.keys(pendingChangesRef.current).length, 'pending changes');
      saveChanges();
    }, 2000);

    return () => clearTimeout(timer);
  }, [pendingChanges]); // saveChanges removed - it reads from ref anyway

  // Update entry field
  const updateEntry = (day, field, value) => {
    // Read from ref to get latest accumulated pending changes (not stale state)
    const current = entries[day] || {};
    const pending = pendingChangesRef.current[day] || {};

    const updated = {
      ...current,
      ...pending,
      [field]: value,
    };

    // Calculate work minutes if times are set
    if (field === 'startTime' || field === 'endTime' || field === 'breakMinutes') {
      const startTime = field === 'startTime' ? value : (updated.startTime || '');
      const endTime = field === 'endTime' ? value : (updated.endTime || '');
      const breakMins = field === 'breakMinutes' ? parseInt(value) || 0 : (parseInt(updated.breakMinutes) || 0);

      if (startTime && endTime) {
        const [startH, startM] = startTime.split(':').map(Number);
        const [endH, endM] = endTime.split(':').map(Number);
        const startMins = startH * 60 + startM;
        const endMins = endH * 60 + endM;
        const workMins = Math.max(0, endMins - startMins - breakMins);
        updated.workMinutes = workMins;
      }
    }

    // When code is cleared, also clear remark (especially "Sync:" remarks)
    if (field === 'code' && (!value || value === '-' || value === '')) {
      updated.remark = '';
    }

    // When code is set to a no-work code, clear working times
    if (field === 'code' && NO_WORK_CODES.includes(value)) {
      updated.startTime = '';
      updated.endTime = '';
      updated.breakMinutes = 0;
      updated.workMinutes = 0;
    }

    // When remark is "MS" (Mutterschutz), auto-set code and clear times
    if (field === 'remark' && value.trim().toUpperCase() === 'MS' && !updated.code) {
      updated.code = 'MS';
      updated.startTime = '';
      updated.endTime = '';
      updated.breakMinutes = 0;
      updated.workMinutes = 0;
    }

    // Update ref immediately for consistency
    pendingChangesRef.current = {
      ...pendingChangesRef.current,
      [day]: updated,
    };

    setEntries(prev => ({
      ...prev,
      [day]: updated,
    }));

    setPendingChanges(prev => ({
      ...prev,
      [day]: updated,
    }));
  };

  // Get entry for a day
  const getEntry = (day) => {
    return entries[day] || {};
  };

  // Check if day is a holiday
  const isHoliday = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidays[dateStr];
  };

  // Format work minutes as hours
  const formatWorkTime = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${String(mins).padStart(2, '0')}`;
  };

  // Navigate months
  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const nextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  // State for downloading all PDFs
  const [downloadingAllPdfs, setDownloadingAllPdfs] = useState(false);

  const saving = bulkUpdateMutation.isPending;
  const syncing = syncMutation.isPending;

  // Sync ALL employees from leaves table and holidays (overwrites existing)
  const handleSync = () => syncMutation.mutate({ year, month });

  // Export all employee PDFs as ZIP
  const handleExportAllPdfs = async () => {
    setDownloadingAllPdfs(true);
    try {
      const response = await api.exportPdfAll({ year, month });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const mm = String(month).padStart(2, '0');
      const yy = String(year).slice(-2);
      link.setAttribute('download', `Stundenzettel ${mm}.${yy}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Alle PDFs heruntergeladen');
    } catch (err) {
      console.error('[Stundenzettel] Export all PDFs error:', err);
      toast.error(err.message || 'Fehler beim Herunterladen aller PDFs');
    } finally {
      setDownloadingAllPdfs(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    if (!employeeId) {
      toast.error('Bitte einen Mitarbeiter auswählen');
      return;
    }

    try {
      const response = await api.exportExcel({
        user_id: employeeId,
        year,
        month,
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or generate default
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `Stundenzettel_${year}-${String(month).padStart(2, '0')}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1].replace(/['"]/g, ''));
        }
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel-Export erstellt');
    } catch (err) {
      console.error('[Stundenzettel] Excel export error:', err);
      toast.error(err.message || 'Fehler beim Excel-Export');
    }
  };

  // Export to PDF
  const handleExportPdf = async () => {
    if (!employeeId) {
      toast.error('Bitte einen Mitarbeiter auswählen');
      return;
    }

    try {
      const response = await api.exportPdf({
        user_id: employeeId,
        year,
        month,
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or generate default
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `Stundenzettel_${year}-${String(month).padStart(2, '0')}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1].replace(/['"]/g, ''));
        }
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF-Export erstellt');
    } catch (err) {
      console.error('[Stundenzettel] PDF export error:', err);
      toast.error(err.message || 'Fehler beim PDF-Export');
    }
  };

  // Calculate totals
  const totals = useMemo(() => {
    let totalWorkMinutes = 0;
    let workDays = 0;
    let sickDays = 0;
    let vacationDays = 0;
    let holidayDays = 0;
    let schoolDays = 0;
    let unpaidDays = 0;
    let otherDays = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const entry = getEntry(day);
      if (entry.workMinutes) {
        totalWorkMinutes += entry.workMinutes;
        workDays++;
      }
      switch (entry.code) {
        case 'K': sickDays++; break;
        case 'U': vacationDays++; break;
        case 'F': holidayDays++; break;
        case 'S': schoolDays++; break;
        case 'UB': unpaidDays++; break;
        case 'E': case 'X': otherDays++; break;
      }
    }

    return {
      totalWorkMinutes,
      totalWorkHours: (totalWorkMinutes / 60).toFixed(1),
      workDays,
      sickDays,
      vacationDays,
      holidayDays,
      schoolDays,
      unpaidDays,
      otherDays,
    };
  }, [entries, daysInMonth]);

  const employeeOptions = employees.map(e => ({
    value: e.user_id,
    label: e.name,
  }));

  // Open calendar modal for employee
  const openCalendarModal = (userId) => {
    setEmployeeId(userId);
    setCalendarModalOpen(true);
  };

  // Close calendar modal
  const closeCalendarModal = () => {
    setCalendarModalOpen(false);
    setPendingChanges({});
    pendingChangesRef.current = {};
    queryClient.invalidateQueries({ queryKey: ['stundenzettel', 'overview'] });
  };

  // Get current employee
  const currentEmployee = employees.find(e => e.user_id === employeeId);

  return (
    <div>
      <PageHeader
        title="Stundenzettel"
        description="Monatliche Arbeitszeiterfassung und Abwesenheiten"
      />

      {/* Month/Year Filter */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="relative min-w-[200px] max-w-sm flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suche..."
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={prevMonth} />
            <Select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              options={yearOptions}
              className="w-28"
            />
            <Select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              options={monthOptions}
              className="w-36"
            />
            <Button variant="ghost" size="sm" icon={ChevronRight} onClick={nextMonth} />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              icon={RefreshCw}
              onClick={handleSync}
              loading={syncing}
              title="Synchronisiert alle Mitarbeiter mit Urlaubs- und Krankmeldungen"
            >
              Sync
            </Button>
            <Button
              variant="primary"
              icon={Download}
              onClick={handleExportAllPdfs}
              loading={downloadingAllPdfs}
            >
              Alle PDFs
            </Button>
          </div>
        </div>
      </Card>

      {/* Overview Table */}
      {loading ? (
        <Card className="p-8 text-center">
          <Spinner />
        </Card>
      ) : (
        <DataTable
          columns={[
            { accessorKey: 'name', header: 'Mitarbeiter', cell: ({ row }) => <span className="font-medium">{row.original.name}</span> },
            { accessorKey: 'personal_number', header: 'Pers.Nr.', cell: ({ row }) => <span className="text-gray-500 font-mono text-xs">{row.original.personal_number || '-'}</span> },
            { accessorKey: 'employment_type', header: 'Typ', cell: ({ row }) => <Badge variant={row.original.employment_type === 'VZ' ? 'primary' : 'secondary'}>{row.original.employment_type || 'VZ'}</Badge> },
            { accessorFn: (row: any) => row.total_work_hours ?? 0, id: 'hours', header: 'Stunden', cell: ({ row }) => <span className="font-semibold">{row.original.total_work_hours?.toFixed(1) || '-'}</span> },
            { accessorFn: (row: any) => row.days_worked ?? 0, id: 'days', header: 'Arbeitstage', cell: ({ row }) => row.original.days_worked || '-' },
            { accessorFn: (row: any) => row.sick_days ?? 0, id: 'sick', header: 'Krank', cell: ({ row }) => row.original.sick_days > 0 ? <Badge variant="danger">{row.original.sick_days}</Badge> : <span className="text-gray-400">-</span> },
            { accessorFn: (row: any) => row.vacation_days ?? 0, id: 'vacation', header: 'Urlaub', cell: ({ row }) => row.original.vacation_days > 0 ? <Badge variant="success">{row.original.vacation_days}</Badge> : <span className="text-gray-400">-</span> },
            { accessorFn: (row: any) => row.other_days ?? 0, id: 'other', header: 'Sonstige', cell: ({ row }) => (row.original.other_days || 0) > 0 ? row.original.other_days : <span className="text-gray-400">-</span> },
          ] as ColumnDef<any>[]}
          data={(() => {
            const arr = Array.isArray(overview) ? overview : [];
            if (!searchTerm) return arr;
            const term = searchTerm.toLowerCase();
            return arr.filter((emp: any) => (emp.name || '').toLowerCase().includes(term) || (emp.personal_number || '').toLowerCase().includes(term));
          })()}
          emptyText={searchTerm ? 'Kein Mitarbeiter gefunden' : 'Keine Daten für diesen Monat'}
          onRowClick={(emp: any) => openCalendarModal(emp.user_id)}
          pageSize={50}
        />
      )}

      {/* Calendar Modal */}
      <Modal
        isOpen={calendarModalOpen}
        onClose={closeCalendarModal}
        title={`Stundenzettel - ${currentEmployee?.name || 'Mitarbeiter'}`}
        size="full"
      >
        <div className="p-4">
          {/* Modal Header with Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <Badge variant={currentEmployee?.employment_type === 'TZ' ? 'secondary' : 'primary'} className="text-sm">
                {currentEmployee?.employment_type || 'VZ'}
              </Badge>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={prevMonth} />
                <Select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  options={yearOptions}
                  className="w-24"
                />
                <Select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  options={monthOptions}
                  className="w-36"
                />
                <Button variant="ghost" size="sm" icon={ChevronRight} onClick={nextMonth} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" icon={FileSpreadsheet} onClick={handleExportExcel} size="sm">
                Excel
              </Button>
              <Button variant="outline" icon={FileText} onClick={handleExportPdf} size="sm">
                PDF
              </Button>
              {Object.keys(pendingChanges).length > 0 && (
                <Button
                  variant="primary"
                  icon={Save}
                  onClick={saveChanges}
                  loading={saving}
                  size="sm"
                >
                  Speichern
                </Button>
              )}
            </div>
          </div>

          {/* Stats Badges */}
          <div className="flex items-center gap-4 mb-4">
            <Badge variant="info" className="px-3 py-1">
              Stunden: {totals.totalWorkHours}
            </Badge>
            <Badge variant="success" className="px-3 py-1">
              Urlaub: {totals.vacationDays} Tage
            </Badge>
            <Badge variant="danger" className="px-3 py-1">
              Krank: {totals.sickDays} Tage
            </Badge>
            {totals.otherDays > 0 && (
              <Badge variant="secondary" className="px-3 py-1">
                Sonstige: {totals.otherDays} Tage
              </Badge>
            )}
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Spinner />
            </div>
          ) : (
            <>
              {/* Calendar Grid (Mo-So Layout) */}
              <div className="bg-white rounded-lg border overflow-hidden">
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-px bg-gray-200">
                  {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
                    <div
                      key={day}
                      className={`text-center font-semibold py-2 ${
                        day === 'Sa' || day === 'So' ? 'bg-gray-100 text-gray-600' : 'bg-primary-50 text-primary-700'
                      }`}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Weeks */}
                <div className="grid gap-px bg-gray-200">
                  {(() => {
                    // Calculate calendar grid
                    const firstDayOfMonth = new Date(year, month - 1, 1);
                    const firstDayWeekday = firstDayOfMonth.getDay();
                    const startOffset = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;

                    const weeks = [];
                    let currentDay = 1;
                    const totalDays = daysInMonth;

                    let weekNum = 0;
                    while (currentDay <= totalDays) {
                      const week = [];
                      for (let dow = 0; dow < 7; dow++) {
                        if (weekNum === 0 && dow < startOffset) {
                          week.push(null);
                        } else if (currentDay > totalDays) {
                          week.push(null);
                        } else {
                          week.push(currentDay);
                          currentDay++;
                        }
                      }
                      weeks.push(week);
                      weekNum++;
                    }

                    return weeks.map((week, weekIndex) => (
                      <div key={weekIndex} className="grid grid-cols-7 gap-px">
                        {week.map((day, dayIndex) => {
                          if (day === null) {
                            return <div key={dayIndex} className="min-h-[90px] bg-gray-50" />;
                          }

                          const entry = getEntry(day);
                          const weekend = dayIndex >= 5;
                          const holiday = isHoliday(day);
                          const effectiveCode = entry.code || (holiday ? 'F' : '');

                          // Determine background color
                          let bgColor = 'bg-white';
                          if (weekend) {
                            bgColor = 'bg-gray-100';
                          } else if (effectiveCode === 'F') {
                            bgColor = 'bg-cyan-50';
                          } else if (effectiveCode === 'U') {
                            bgColor = 'bg-green-50';
                          } else if (effectiveCode === 'K') {
                            bgColor = 'bg-yellow-50';
                          } else if (effectiveCode === 'UB') {
                            bgColor = 'bg-orange-50';
                          } else if (effectiveCode === 'Ü') {
                            bgColor = 'bg-blue-50';
                          } else if (effectiveCode === 'S' || effectiveCode === 'E' || effectiveCode === 'X') {
                            bgColor = 'bg-purple-50';
                          }

                          return (
                            <div
                              key={dayIndex}
                              className={`min-h-[90px] p-2 ${bgColor} flex flex-col`}
                            >
                              {/* Date header */}
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-sm font-medium ${weekend ? 'text-gray-500' : 'text-gray-800'}`}>
                                  {String(day).padStart(2, '0')}.
                                </span>
                                {holiday && (
                                  <span className="text-xs text-cyan-600 truncate max-w-[50px]" title={holiday}>
                                    {holiday.length > 6 ? holiday.substring(0, 5) + '..' : holiday}
                                  </span>
                                )}
                              </div>

                              {/* Code dropdown (only for weekdays) */}
                              {!weekend && (
                                <select
                                  value={effectiveCode}
                                  onChange={(e) => updateEntry(day, 'code', e.target.value)}
                                  className={`w-full px-1 py-1 text-xs border rounded mb-1 ${
                                    effectiveCode ? 'font-semibold' : ''
                                  } focus:ring-1 focus:ring-primary-500 bg-white`}
                                >
                                  {CODE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              )}

                              {/* Code display badge */}
                              {effectiveCode && (
                                <div className="text-center mb-1">
                                  <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded ${
                                    effectiveCode === 'K' ? 'bg-yellow-200 text-yellow-800' :
                                    effectiveCode === 'U' ? 'bg-green-200 text-green-800' :
                                    effectiveCode === 'F' ? 'bg-cyan-200 text-cyan-800' :
                                    effectiveCode === 'UB' ? 'bg-orange-200 text-orange-800' :
                                    effectiveCode === 'Ü' ? 'bg-blue-200 text-blue-800' :
                                    'bg-purple-200 text-purple-800'
                                  }`}>
                                    {effectiveCode}
                                  </span>
                                </div>
                              )}

                              {/* Remark field (only for weekdays) */}
                              {!weekend && (
                                <input
                                  type="text"
                                  value={entry.remark || ''}
                                  onChange={(e) => updateEntry(day, 'remark', e.target.value)}
                                  placeholder="Bemerkung"
                                  className="w-full px-1 py-0.5 text-xs border rounded mt-auto focus:ring-1 focus:ring-primary-500"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Legend */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="font-medium text-gray-700">Legende:</span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-4 h-4 bg-cyan-100 border border-cyan-300 rounded"></span>
                    F = Feiertag
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-4 h-4 bg-green-100 border border-green-300 rounded"></span>
                    U = Urlaub
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></span>
                    K = Krank
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-4 h-4 bg-orange-100 border border-orange-300 rounded"></span>
                    UB = Unbez. Urlaub
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-4 h-4 bg-purple-100 border border-purple-300 rounded"></span>
                    S = Sonstiges / E = Elternzeit / X = Ausgeschieden
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-4 h-4 bg-blue-200 border border-blue-300 rounded"></span>
                    Ü = Überstunden (nicht im PDF)
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-4 h-4 bg-gray-200 border border-gray-300 rounded"></span>
                    Wochenende
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Stundenzettel;
